import SelectedCheckboxSVG from '@assets/icons/checkbox-selected.svg?react';
import UnselectedCheckboxSVG from '@assets/icons/checkbox-unselected.svg?react';
import { ColorKeys } from '@common/types/theme';
import { InvertedRadio, InvertedRadioChecked } from '@components/utility/radios';
import { palette } from '@constants/theme';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { alpha, createTheme, responsiveFontSizes } from '@mui/material';
import { ChipPropsVariantOverrides } from '@mui/material/Chip';
import type {} from '@mui/x-date-pickers/themeAugmentation';

const rootElement = document.getElementById('root')!;

const createFontFamily = (fontFamily: string) => ({
    h1: { fontFamily },
    h2: { fontFamily },
    h3: { fontFamily },
    h4: { fontFamily },
    h5: { fontFamily },
    h6: { fontFamily },
    subtitle1: { fontFamily },
    subtitle2: { fontFamily },
    body1: { fontFamily },
    body2: { fontFamily },
    button: { fontFamily },
    caption: { fontFamily },
    overline: { fontFamily },
});

const paletteTheme = createTheme({
    palette: {
        primary: {
            main: `#${palette['primary-600']}`,
        },
        secondary: {
            main: `#${palette['secondary-500']}`,
            contrastText: '#FFFFFF',
        },
        success: {
            main: `#${palette['confirm-500']}`,
            contrastText: '#fff',
        },
        error: {
            main: `#${palette['error-500']}`,
        },
        warning: {
            main: `#${palette['warning-500']}`,
        },
    },
    typography: {
        button: {
            textTransform: 'none',
        },
        allVariants: {
            fontFamily: 'Inter',
        },
    },
});

const extendedTheme = createTheme(paletteTheme, {
    palette: {
        black: paletteTheme.palette.augmentColor({
            color: {
                main: '#000',
            },
            name: 'black',
        }),
        white: paletteTheme.palette.augmentColor({
            color: {
                main: '#FFF',
                light: '#FFFFFF90',
            },
            name: 'white',
        }),
        'light-blue': paletteTheme.palette.augmentColor({
            color: {
                main: '#2196F3',
            },
            name: 'light-blue',
        }),
        'info-dark': paletteTheme.palette.augmentColor({
            color: {
                main: '#667085',
            },
            name: 'info-dark',
        }),
        'secondary-alt': paletteTheme.palette.augmentColor({
            color: {
                main: '#E6E6E6',
                light: '#FAF9F5',
                contrastText: '#231F20CC',
            },
            name: 'secondary-alt',
        }),
        'warning-alt': paletteTheme.palette.augmentColor({
            color: {
                main: '#FF9900',
                dark: '#FA8700',
                contrastText: '#fff',
            },
            name: 'warning-alt',
        }),
        'victron-blue': paletteTheme.palette.augmentColor({
            color: {
                main: '#1976D2',
            },
            name: 'victron-blue',
        }),
        'alert-info-bg': paletteTheme.palette.augmentColor({
            color: {
                main: '#E5F6FD',
                contrastText: '#014361',
            },
            name: 'alert-info-bg',
        }),
        'light-success': paletteTheme.palette.augmentColor({
            color: {
                main: '#EDF7ED',
                contrastText: '#1E4620',
            },
            name: 'light-success',
        }),
        'light-confirm': paletteTheme.palette.augmentColor({
            color: {
                main: '#EDF7ED',
                contrastText: '#1E4620',
            },
            name: 'light-confirm',
        }),
        'light-warning': paletteTheme.palette.augmentColor({
            color: {
                main: '#FFF4E5',
                contrastText: '#663C00',
            },
        }),
        'light-error': paletteTheme.palette.augmentColor({
            color: {
                main: '#FDEDED',
                contrastText: '#5F2120',
            },
            name: 'light-error',
        }),
        'status-success': paletteTheme.palette.augmentColor({
            color: {
                main: '#EBF8EF',
                contrastText: '#4ABD9E',
            },
            name: 'status-success',
        }),
        'status-error': paletteTheme.palette.augmentColor({
            color: {
                main: '#FFF5F3',
                contrastText: '#CB524A',
            },
            name: 'status-error',
        }),
        gray44: paletteTheme.palette.augmentColor({
            color: {
                main: '#707070',
            },
            name: 'gray44',
        }),
        'check-confirm': paletteTheme.palette.augmentColor({
            color: {
                main: '#2E7D32',
            },
            name: 'check-confirm',
        }),
        'info-main': paletteTheme.palette.augmentColor({
            color: {
                main: '#0288D1',
            },
            name: 'info-main',
        }),
        gray: paletteTheme.palette.augmentColor({
            color: {
                main: '#404040',
            },
            name: 'gray',
        }),
        'dark-yellow': paletteTheme.palette.augmentColor({
            color: {
                main: '#663C00',
            },
            name: 'dark-yellow',
        }),
        'gray-1': paletteTheme.palette.augmentColor({
            color: {
                main: '#333333',
            },
            name: 'gray-1',
        }),
        gray51: paletteTheme.palette.augmentColor({
            color: {
                main: '#828282',
            },
            name: 'gray51',
        }),
        'dark-red': paletteTheme.palette.augmentColor({
            color: {
                main: '#5F2120',
            },
            name: 'dark-red',
        }),
        'dark-green': paletteTheme.palette.augmentColor({
            color: {
                main: '#1E4620',
            },
            name: 'dark-green',
        }),
        'alert-info': paletteTheme.palette.augmentColor({
            color: {
                main: '#014361',
            },
            name: 'alert-info',
        }),
        rajah: paletteTheme.palette.augmentColor({
            color: {
                main: '#FCB8591F',
                contrastText: '#663C00',
            },
        }),
        'persian-red': paletteTheme.palette.augmentColor({
            color: {
                main: '#D32F2F',
            },
        }),
        beer: paletteTheme.palette.augmentColor({
            color: {
                main: '#F79816',
            },
        }),
        'action-active': paletteTheme.palette.augmentColor({
            color: {
                main: '#0000008F',
            },
        }),
        black40: paletteTheme.palette.augmentColor({
            color: {
                main: '#00000066',
            },
        }),
        disabled: paletteTheme.palette.augmentColor({
            color: {
                main: alpha('#000000', 0.08),
                contrastText: alpha('#000000', 0.27),
            },
        }),
        'sonic-silver': paletteTheme.palette.augmentColor({
            color: {
                main: '#707579',
            },
        }),
        'signal-warning': paletteTheme.palette.augmentColor({
            color: {
                main: '#DB3500',
            },
        }),
        'light-informative-border': paletteTheme.palette.augmentColor({
            color: {
                main: '#9DC0EE',
            },
        }),
        'light-informative-background': paletteTheme.palette.augmentColor({
            color: {
                main: '#F5F9FF',
            },
        }),
        'light-toast-title': paletteTheme.palette.augmentColor({
            color: {
                main: '#27303A',
            },
        }),
        'light-toast-message': paletteTheme.palette.augmentColor({
            color: {
                main: '#2F3F53',
            },
        }),
        'light-error-background': paletteTheme.palette.augmentColor({
            color: {
                main: '#FFF5F3',
            },
        }),
        'light-error-border': paletteTheme.palette.augmentColor({
            color: {
                main: '#F4B0A1',
            },
        }),
        'light-success-background': paletteTheme.palette.augmentColor({
            color: {
                main: '#F6FFF9',
            },
        }),
        'light-success-border': paletteTheme.palette.augmentColor({
            color: {
                main: '#48C1B5',
            },
        }),
        'light-warning-background': paletteTheme.palette.augmentColor({
            color: {
                main: '#FFF8EC',
            },
        }),
        'light-warning-border': paletteTheme.palette.augmentColor({
            color: {
                main: '#F7D9A4',
            },
        }),
        ...Object.keys(palette)
            .filter((colorKey) => palette[colorKey as ColorKeys] !== '')
            .map((colorKey) => ({
                [colorKey]: paletteTheme.palette.augmentColor({
                    color: {
                        main: palette[colorKey as ColorKeys].startsWith('#')
                            ? palette[colorKey as ColorKeys]
                            : `#${palette[colorKey as ColorKeys]}`,
                    },
                    name: colorKey,
                }),
            }))
            .reduce((acc, curr) => ({ ...acc, ...curr }), {}),
    },
});

let theme = createTheme(extendedTheme, {
    typography: {
        fontFamily: 'Inter, Arial, sans-serif',
        button: {
            textTransform: 'none',
        },
    },
    components: {
        MuiChip: {
            variants: [
                {
                    props: { variant: 'status-success' },
                    style: {
                        backgroundColor: extendedTheme.palette['status-success'].main,
                        color: extendedTheme.palette['status-success'].contrastText,
                        border: '1px solid currentColor',
                    },
                },
                {
                    props: { variant: 'status-error' },
                    style: {
                        backgroundColor: extendedTheme.palette['status-error'].main,
                        color: extendedTheme.palette['status-error'].contrastText,
                        border: '1px solid currentColor',
                    },
                },
            ],
        },
        MuiPopover: {
            defaultProps: {
                container: rootElement,
            },
        },
        MuiPopper: {
            defaultProps: {
                container: rootElement,
            },
        },
        MuiModal: {
            defaultProps: {
                container: rootElement,
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: '#fff',
                    '&.Mui-disabled': {
                        background: '#F1F1F1',
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderColor: paletteTheme.palette.action.disabled + ' !important',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: paletteTheme.palette.primary.main + '80',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: paletteTheme.palette.primary.main,
                    },
                },
                notchedOutline: {
                    borderColor: '#D0D5DD',
                },
            },
        },
        MuiAutocomplete: {
            defaultProps: {
                popupIcon: <KeyboardArrowDownIcon />,
            },
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        padding: '7px 9px',
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    borderRadius: 2,
                },
            },
            defaultProps: {
                IconComponent: KeyboardArrowDownIcon,
            },
        },
        MuiDatePicker: {
            defaultProps: {
                slots: {
                    openPickerIcon: CalendarTodayOutlinedIcon,
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        backgroundColor: '#E3F0FF',
                        '&:hover': {
                            backgroundColor: '#E3F0FF',
                        },
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    letterSpacing: '-0.025em',
                    fontSize: 16,
                    fontWeight: 600,
                },
                sizeXl: {
                    padding: '12px 24px',
                },
            },
            variants: [
                {
                    props: { variant: 'contained' },
                    style: {
                        border: '1px solid #000',
                    },
                },
            ],
            defaultProps: {
                disableElevation: true,
                variant: 'contained',
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    color: extendedTheme.palette['neutral-900'].main,
                    fontSize: 16,
                    letterSpacing: 0.15,
                    '&::placeholder': {
                        color: extendedTheme.palette['neutral-700'].main,
                        opacity: 1,
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    padding: 0,
                    minHeight: 'unset',
                    '&.Mui-expanded': {
                        minHeight: 'unset',
                    },
                },
                content: {
                    fontFamily: 'Inter',
                    letterSpacing: '-0.025em',
                    fontWeight: 800,
                    fontSize: 12,
                    lineHeight: '26px',
                    margin: 0,
                    '&.Mui-expanded': {
                        margin: 0,
                    },
                },
                expandIconWrapper: {
                    color: 'black',
                },
            },
            defaultProps: {
                expandIcon: <ExpandMoreIcon />,
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    padding: 0,
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    '&::before': {
                        background: 'none',
                    },
                    '&.Mui-expanded': {
                        margin: 0,
                    },
                },
            },
            defaultProps: {
                defaultExpanded: true,
                elevation: 0,
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontWeight: 400,
                    fontSize: 16,
                },
                asterisk: {
                    color: '#f44336',
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                asterisk: {
                    color: '#f44336',
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontFamily: 'Roboto,Helvetica,Arial,sans-serif',
                    fontSize: 10,
                    fontWeight: 500,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    maxWidth: 'none',
                    minWidth: '60%',
                    width: 'calc(100% - 64px)',
                    [extendedTheme.breakpoints.down('md')]: {
                        width: 'calc(100% - 16px)',
                        margin: '12px',
                    },
                },
            },
            defaultProps: {
                container: rootElement,
                disableEscapeKeyDown: true,
                slotProps: {
                    backdrop: { timeout: 500 },
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '& .MuiListItemIcon-root': {
                        minWidth: 'unset',
                        marginRight: 5,
                    },
                },
            },
        },
        MuiAppBar: {
            defaultProps: {
                elevation: 0,
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    padding: 0,
                    minHeight: 50,
                    '@media (min-width: 600px)': {
                        padding: 0,
                        minHeight: 50,
                    },
                },
            },
        },
        MuiDrawer: {
            defaultProps: {
                slotProps: {
                    paper: {
                        sx: {
                            border: 'none',
                        },
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    scrollMargin: 200,
                },
            },
        },
        MuiTabs: {
            defaultProps: {
                slotProps: {
                    indicator: {
                        sx: {
                            display: 'none',
                        },
                    },
                },
            },
            styleOverrides: {
                root: {
                    minHeight: 31,
                },
                flexContainer: {
                    gap: 3,
                },
            },
        },
        MuiCheckbox: {
            defaultProps: {
                icon: <UnselectedCheckboxSVG />,
                checkedIcon: <SelectedCheckboxSVG />,
            },
            styleOverrides: {
                root: {
                    '&.Mui-disabled.Mui-checked rect': {
                        fill: '#00000029',
                        stroke: 'transparent',
                    },
                    '&.Mui-disabled.Mui-checked path': {
                        fill: '#00000029',

                        stroke: '#00000029',
                        strokeWidth: '1.2px',
                    },
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                    '&.Mui-disabled:not(.Mui-checked) path': {
                        display: 'none',
                    },
                    '&.Mui-disabled:not(.Mui-checked) svg': {
                        overflow: 'hidden',
                        width: '20px',
                        height: '20px',
                        borderRadius: '4px',
                    },
                    '&.Mui-disabled:not(.Mui-checked) rect': {
                        fill: '#00000020',
                        stroke: '#00000029',
                        strokeWidth: '2px',
                    },
                },
            },
        },
        MuiRadio: {
            defaultProps: {
                icon: <InvertedRadio />,
                checkedIcon: <InvertedRadioChecked />,
            },
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                    '&.Mui-disabled.Mui-checked circle:first-of-type': {
                        stroke: '#00000029',
                    },
                    '&.Mui-disabled.Mui-checked circle:last-of-type': {
                        fill: '#00000029',
                    },
                    '&.Mui-disabled:not(.Mui-checked) circle': {
                        stroke: '#00000029',
                    },
                },
            },
        },
        MuiMultiSectionDigitalClockSection: {
            styleOverrides: {
                root: {
                    'scrollbar-width': 'none',
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                },
            },
        },
        MuiCard: {
            variants: [
                {
                    props: { variant: 'elevation' },
                    style: {
                        boxShadow: '0 0 1px 0 rgba(33, 37, 41, 0.08), 0 2px 2px 0 rgba(33, 37, 41, 0.06)',
                    },
                },
            ],
        },
        MuiAlertTitle: {
            styleOverrides: {
                root: {
                    fontFamily: 'Poppins',
                    fontWeight: 600,
                    fontSize: 14,
                    color: extendedTheme.palette['light-toast-title'].main,
                },
            },
        },
    },
});

theme = responsiveFontSizes(theme);

const robotoTheme = createTheme(theme, {
    typography: createFontFamily('Roboto'),
});

export { createFontFamily, robotoTheme, theme };
